import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Users, Clock, CheckCircle, BarChart2, Check } from 'lucide-react';
import api from '../../utils/api';

const NoticeStatsModal = ({ isOpen, onClose, notice }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('viewed');

    useEffect(() => {
        if (isOpen && notice) {
            fetchStats();
        }
    }, [isOpen, notice]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/notices/${notice.id}/stats`);
            setStats(response.data.stats);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-opacity">
            <div className="bg-white w-full h-full md:h-auto md:max-h-[80vh] md:max-w-2xl md:rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 flex items-center justify-between safe-area-top">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <BarChart2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-gray-800">Notice Statistics</h2>
                            <p className="text-xs md:text-sm text-gray-600 truncate max-w-[200px] md:max-w-md">{notice?.title}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50">
                    {loading ? (
                        <div className="flex items-center justify-center p-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : stats ? (
                        <div className="bg-white m-4 md:m-6 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
                                <div className="p-4 text-center">
                                    <div className="text-sm text-gray-500 mb-1">Total</div>
                                    <div className="text-xl md:text-2xl font-bold text-gray-800">{stats.total}</div>
                                </div>
                                <div className="p-4 text-center">
                                    <div className="text-sm text-gray-500 mb-1">Viewed</div>
                                    <div className="text-xl md:text-2xl font-bold text-green-600">{stats.viewed}</div>
                                </div>
                                <div className="p-4 text-center">
                                    <div className="text-sm text-gray-500 mb-1">Pending</div>
                                    <div className="text-xl md:text-2xl font-bold text-amber-600">{stats.notViewed}</div>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="px-4 py-4 md:px-6 border-b border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">View Rate</span>
                                    <span className="text-sm font-bold text-gray-800">
                                        {stats.total > 0 ? Math.round((stats.viewed / stats.total) * 100) : 0}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-green-600 h-2.5 rounded-full transition-all duration-500"
                                        style={{ width: `${stats.total > 0 ? (stats.viewed / stats.total) * 100 : 0}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="px-4 pt-4 md:px-6">
                                <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
                                    <button
                                        onClick={() => setActiveTab('viewed')}
                                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'viewed'
                                            ? 'border-green-500 text-green-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Eye className="w-4 h-4" />
                                            Viewed ({stats.viewed})
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('notViewed')}
                                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'notViewed'
                                            ? 'border-amber-500 text-amber-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <EyeOff className="w-4 h-4" />
                                            Not Viewed ({stats.notViewed})
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Employee List */}
                            <div className="p-4 md:p-6">
                                {activeTab === 'viewed' ? (
                                    stats.viewedEmployees.length === 0 ? (
                                        <p className="text-center text-gray-500 py-8 text-sm">No employees have viewed this notice yet.</p>
                                    ) : (
                                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                                            {stats.viewedEmployees.map((record, index) => (
                                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold">
                                                            {record.first_name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-800 text-sm">
                                                                {record.first_name} {record.last_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">{new Date(record.viewed_at).toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                    <div className="p-1.5 bg-green-100 rounded text-green-700" title="Viewed">
                                                        <Check className="w-4 h-4" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    stats.notViewedEmployees.length === 0 ? (
                                        <p className="text-center text-gray-500 py-8 text-sm">All employees have viewed this notice.</p>
                                    ) : (
                                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                                            {stats.notViewedEmployees.map((employee, index) => (
                                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold">
                                                            {employee.first_name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-800 text-sm">{employee.first_name} {employee.last_name}</p>
                                                            <p className="text-xs text-gray-500">{employee.position || 'Employee'}</p>
                                                        </div>
                                                    </div>
                                                    <div className="p-1.5 bg-gray-100 rounded text-gray-400" title="Not Viewed">
                                                        <EyeOff className="w-4 h-4" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="p-12 text-center">
                            <p className="text-gray-500">Failed to load statistics</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 bg-gray-50 safe-area-bottom">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors text-sm md:text-base"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoticeStatsModal;
